import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  fetchRoster,
  startAttempt,
  fetchNextQuestion,
  submitAnswer,
  finishAttempt,
  type RosterEntry,
  type SafeQuestion,
  type AttemptResult,
} from "./shared/api";
import { RadarChart } from "./shared/RadarChart";
import { Logo } from "./shared/Logo";
import "./participant.css";

const STORAGE_KEY = "diagnostico:attempt";
const TOTAL_QUESTIONS = 24;

type Screen =
  | { step: "loading" }
  | { step: "picker"; roster: RosterEntry[] }
  | { step: "question"; question: SafeQuestion; answeredSoFar: number }
  | { step: "feedback"; isCorrect: boolean; explanation: string; answeredSoFar: number }
  | { step: "result"; result: AttemptResult }
  | { step: "error"; message: string };

interface StoredAttempt {
  attemptId: number;
  answeredCount: number;
}

function saveAttempt(attemptId: number, answeredCount: number) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ attemptId, answeredCount }));
}

function loadAttempt(): StoredAttempt | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof parsed.attemptId === "number" &&
      typeof parsed.answeredCount === "number"
    ) {
      return parsed as StoredAttempt;
    }
    return null;
  } catch {
    return null;
  }
}

function App() {
  const [screen, setScreen] = useState<Screen>({ step: "loading" });
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [answeredCount, setAnsweredCount] = useState(0);

  async function loadNext(id: number, answeredSoFar: number) {
    const next = await fetchNextQuestion(id);
    if (next.done) {
      const result = await finishAttempt(id);
      setScreen({ step: "result", result });
      return;
    }
    setScreen({ step: "question", question: next.question!, answeredSoFar });
  }

  useEffect(() => {
    const existing = loadAttempt();
    if (existing) {
      setAttemptId(existing.attemptId);
      setAnsweredCount(existing.answeredCount);
      loadNext(existing.attemptId, existing.answeredCount).catch((e) =>
        setScreen({ step: "error", message: String(e) })
      );
      return;
    }
    fetchRoster()
      .then((roster) => setScreen({ step: "picker", roster }))
      .catch((e) => setScreen({ step: "error", message: String(e) }));
  }, []);

  async function handlePick(personId: number) {
    const id = await startAttempt(personId);
    saveAttempt(id, 0);
    setAttemptId(id);
    await loadNext(id, 0);
  }

  async function handleAnswer(questionId: number, chosenIndex: number) {
    if (!attemptId) return;
    const result = await submitAnswer(attemptId, questionId, chosenIndex);
    const newCount = answeredCount + 1;
    setAnsweredCount(newCount);
    saveAttempt(attemptId, newCount);
    setScreen({
      step: "feedback",
      isCorrect: result.is_correct,
      explanation: result.explanation,
      answeredSoFar: newCount,
    });
  }

  async function handleContinue() {
    if (!attemptId) return;
    await loadNext(attemptId, answeredCount);
  }

  if (screen.step === "loading") return <p className="status">Cargando...</p>;
  if (screen.step === "error") return <p className="status error">Ocurrió un error: {screen.message}</p>;

  if (screen.step === "picker") {
    return (
      <div className="card">
        <div className="sentinel-header">
          <Logo height={56} showText={true} />
        </div>
        <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
          Selecciona tu nombre para comenzar. Son 24 preguntas de casos cotidianos.
        </p>
        <select defaultValue="" onChange={(e) => e.target.value && handlePick(Number(e.target.value))}>
          <option value="" disabled>
            Elige tu nombre...
          </option>
          {screen.roster.map((p) => (
            <option key={p.id} value={p.id}>
              {p.full_name}
              {p.status === "done" ? " (ya terminó)" : ""}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (screen.step === "question") {
    return (
      <div className="card">
        <div className="sentinel-header">
          <Logo height={42} showText={true} />
        </div>
        <progress value={screen.answeredSoFar} max={TOTAL_QUESTIONS} />
        <p className="progress-label">
          Pregunta {screen.answeredSoFar + 1} de {TOTAL_QUESTIONS}
        </p>
        <h2 style={{ fontSize: "1.2rem", lineHeight: "1.4", margin: "4px 0" }}>{screen.question.prompt}</h2>
        <div className="options">
          {screen.question.options.map((opt, i) => (
            <button key={i} onClick={() => handleAnswer(screen.question.id, i)}>
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (screen.step === "feedback") {
    return (
      <div className="card">
        <div className="sentinel-header">
          <Logo height={42} showText={true} />
        </div>
        <progress value={screen.answeredSoFar} max={TOTAL_QUESTIONS} />
        <h2 className={screen.isCorrect ? "correct" : "incorrect"} style={{ margin: "4px 0" }}>
          {screen.isCorrect ? "✅ Correcto" : "❌ Incorrecto"}
        </h2>
        <p style={{ lineHeight: "1.5", color: "var(--color-text-main)", margin: 0 }}>{screen.explanation}</p>
        <button className="btn-primary" onClick={handleContinue}>Siguiente</button>
      </div>
    );
  }

  const { result } = screen;
  return (
    <div className="card">
      <div className="sentinel-header">
        <Logo height={48} showText={true} />
      </div>
      <h2 style={{ margin: 0 }}>¡Terminaste!</h2>
      <p className="score" style={{ margin: 0 }}>
        Puntaje: {result.score} / {TOTAL_QUESTIONS} — Puesto {result.rank} de {result.total_participants}
      </p>
      <RadarChart points={result.radar.map((r) => ({ label: r.label, pct: r.pct }))} />
      <p style={{ textAlign: "center", margin: 0 }}>
        <a href="/tablero">Ver el tablero del equipo →</a>
      </p>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);

